import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReportingPdrApv from './ReportingPdrApv';
import ReportingGlobal from './ReportingGlobal';
import ReportingVN from './ReportingVN';
import ReportingStockVO from './ReportingStockVO';
import ReportingMarche from './ReportingMarche';
import ReportingBudget from './ReportingBudget';
import reportingFinancierApi from '../services/reporting-financier';
import './ReportingFinancier.css';

// Shell du Reporting Financier.
//
// Les filtres (période + concession) sont gérés AU NIVEAU DU SHELL et
// transmis à chaque onglet via props. Permet à un manager de fixer la
// période une fois et de naviguer entre Global / VN / VO / APV / PDR / TRX
// / ADMIN sans la ressaisir.
//
// L'onglet "Global" est actif par défaut : vue de synthèse cliquable des
// 6 services, points d'attention, total CA HT.

const TOP_TABS = [
  { id: 'global', icon: '🌐', label: 'Global' },
  { id: 'marche', icon: '📈', label: 'Marché'  },
  { id: 'vn',     icon: '🚗', label: 'VN'     },
  { id: 'vo',     icon: '🚙', label: 'VO'     },
  { id: 'stockvn',icon: '📦', label: 'Stock VN' },
  { id: 'stockvo',icon: '📦', label: 'Stock VO' },
  { id: 'apv',    icon: '🔧', label: 'APV'    },
  { id: 'pdr',    icon: '🛠️', label: 'PDR'    },
  { id: 'trx',    icon: '🏭', label: 'Trav.Ext.' },
  { id: 'admin',  icon: '📊', label: 'ADMIN'  },
  { id: 'budget', icon: '💰', label: 'Budget' },
];

function defaultPeriode() {
  const now = new Date();
  const debut = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    date_debut: debut.toISOString().slice(0, 10),
    date_fin:   now.toISOString().slice(0, 10),
  };
}

// Dropdown multi-sélection (réutilisé dans le shell).
function MultiSelect({ options, selected, onChange, allLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const label = selected.length === 0
    ? allLabel
    : selected.length === 1
      ? selected[0]
      : `${selected.length} concessions`;
  return (
    <div className="rf-ms" ref={ref}>
      <button
        type="button"
        className={`page-select rf-ms-btn ${selected.length ? 'active-filter' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="rf-ms-label">{label}</span>
        <span className="rf-ms-caret">▾</span>
      </button>
      {open && (
        <div className="rf-ms-panel">
          <button type="button" className="rf-ms-all" onClick={() => onChange([])}>
            {allLabel}
          </button>
          <div className="rf-ms-options">
            {options.map(opt => (
              <label key={opt} className="rf-ms-option">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportingFinancier({ user, setHeaderAction }) {
  const [topTab, setTopTab] = useState('global');

  // Filtres partagés tous onglets (remontés au shell).
  const def = useMemo(() => defaultPeriode(), []);
  const [dateDebut,   setDateDebut]   = useState(def.date_debut);
  const [dateFin,     setDateFin]     = useState(def.date_fin);
  const [concessions, setConcessions] = useState([]);

  // Valeurs distinctes pour le filtre concession (union APV/PDR/TRX).
  const [dimensions, setDimensions] = useState({ concessions: [], departments: [], marques: [] });
  useEffect(() => {
    reportingFinancierApi.getGlobalDimensions()
      .then(r => setDimensions(r.data || { concessions: [], departments: [], marques: [] }))
      .catch(() => {});
  }, []);

  // Reset le header action quand le shell est démonté.
  useEffect(() => {
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [setHeaderAction]);

  const handleNavigateToService = useCallback((serviceCode) => {
    // Mapping code service backend → id onglet shell
    const map = { VN: 'vn', VO: 'vo', APV: 'apv', PDR: 'pdr', TRX: 'trx', ADMIN: 'admin' };
    if (map[serviceCode]) setTopTab(map[serviceCode]);
  }, []);

  const renderContent = () => {
    const common = {
      user,
      setHeaderAction,
      dateDebut, dateFin, concessions,
      onChangeDateDebut:   setDateDebut,
      onChangeDateFin:     setDateFin,
      onChangeConcessions: setConcessions,
      dimensions,
    };
    if (topTab === 'global') {
      return <ReportingGlobal {...common} onNavigateToService={handleNavigateToService} />;
    }
    if (topTab === 'vn') return <ReportingVN type="vn" {...common} />;
    if (topTab === 'vo') return <ReportingVN type="vo" {...common} />;
    if (topTab === 'stockvo') return <ReportingStockVO {...common} />;
    if (topTab === 'stockvn') return <ReportingStockVO type="vn" {...common} />;
    if (topTab === 'marche') return <ReportingMarche {...common} />;
    if (topTab === 'apv') return <ReportingPdrApv type="APV" {...common} />;
    if (topTab === 'pdr') return <ReportingPdrApv type="PDR" {...common} />;
    if (topTab === 'trx') return <ReportingPdrApv type="TRX" {...common} />;
    if (topTab === 'budget') return <ReportingBudget user={user} setHeaderAction={setHeaderAction} />;
    const current = TOP_TABS.find(t => t.id === topTab);
    return (
      <div className="rf-placeholder">
        <h3>{current?.icon} {current?.label}</h3>
        <p>Section en cours de construction. Les données seront branchées prochainement.</p>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="page-tabs">
        {TOP_TABS.map(t => (
          <button
            key={t.id}
            className={topTab === t.id ? 'active' : ''}
            onClick={() => setTopTab(t.id)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar partagée : période + concession, applicables à tous les onglets. */}
      <div className="rf-shared-toolbar">
        <label className="rf-field">
          <span>Du</span>
          <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
        </label>
        <label className="rf-field">
          <span>Au</span>
          <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} />
        </label>
        <MultiSelect
          options={dimensions.concessions || []}
          selected={concessions}
          onChange={setConcessions}
          allLabel="Toutes concessions"
        />
      </div>

      <div className="rf-content">{renderContent()}</div>
    </div>
  );
}

export default ReportingFinancier;
